import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';

// we can add more properties to the theme object if needed, copied over from safepath-mobile.
const theme = {
    colors: {
        primary: '#184A89', // '#004b87', old slightly darker blue
        secondary: '#8DC7EE', //'#8b1f41', old dark pink
        default: {
            //text: '#6b7280',
            border: '#e5e7eb',
            background: '#f9fafb',
        },
        info: {
            text: '#1d4ed8',
            border: '#bfdbfe',
            background: '#eff6ff',
        },
        secondaryInfo: {
            text: '#374151',
            border: '#004b87',
            background: '#eff6ff',
        },
        alert: {
            text: '#D82B01', //'#b91c1c', old darker red
            border: '#fecaca',
            background: '#fef2f2',
        },
        warning: {
            text: '#ca8a04',
            border: '#fef08a',
            background: '#fefce8',
        },
        success: {
            text: '#16a34a',
            border: '#bbf7d0',
            background: '#f0fdf4',
        },
        approved: {
            text: '#166534', //dark green
            background: '#dcfce7',
        },
        pending: {
            text: '#854d0e',
            background: '#fef9c3',
        },
        verified: {
            text: '#1e40af',
            background: '#dbeafe',
        },
        icon: {
            color: "#004b87"
        },
        //extra colors
        active: {
            background: "#d1fae5", // bg-emerald-100
            text: "#065f46", // text-emerald-800
            border: "1px solid rgba(16, 185, 129, 0.2)",
        },
        forfeit: {
            background: "#fecaca", // bg-red-100
            text: "#b91c1c", // text-red-800
            border: "1px solid rgba(239, 68, 68, 0.2)", // ring-red-600/20
        },
        conditionsMet: {
            background: "#059669", // bg-emerald-600
            text: "#ffffff", // text-white
            border: "1px solid rgba(16, 185, 129, 0.2)", // ring-emerald-600/20
        },

        title: {
            fontSize: "1.5rem",
            fontWeight: 700,
        },

        //Style helpers
        flexRow: {
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
        },
        centeredColumn: {
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            flex: 1,
        }, 

        //Cards and Buttons
        card: {
            backgroundColor: '#ffffff',
            borderWidth: 1,
            borderRadius: 8,
            padding: 12,
            marginHorizontal: 16,
            marginBottom: 16,
            shadowColor: '#000',
            shadowOffset: { width: 0, height: 1 },
            shadowOpacity: 0.1,
            shadowRadius: 2,
            elevation: 2,
        },
        cardHeader: {
            flexDirection: 'row',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: 10,
        },
        cardHeaderText: {
            fontSize: 20,
            fontWeight: '600',
        },
        headerButtonStyle: {
            display: "flex",
            alignItems: "center",
            backgroundColor: "#004b87",
            color: "#fff",
            borderRadius: "4px",
            cursor: "pointer",
        },
        headerButton: {
            flexDirection: 'row',
            alignItems: 'center',
            padding: "8px 12px",
            borderRadius: 4,
            backgroundColor: '#004b87',
            color: '#fff',
            fontSize: 13,
        },
        headerButtonGreen: {
            display: "flex",
            alignItems: "center",
            backgroundColor: '#16a34a',
            borderRadius: "4px",
            color: '#fff',
            cursor: "pointer",
        },
        headerButtonRed: {
            display: "flex",
            alignItems: "center",
            backgroundColor: '#ff3403',
            borderRadius: "4px",
            color: '#fff',
            cursor: "pointer",
        },
        formGroup: {
            marginBottom: "16px", // mb-4
            flex: 1,
        },
        label: {
            display: "block",
            fontSize: "14px", // text-sm
            fontWeight: "500", // font-medium
            color: "#374151", // text-gray-700
        },
        input: {
            marginTop: "4px", // mt-1
            display: "block",
            width: "100%",
            padding: "8px", // p-2
            border: "1px solid #d1d5db", // border border-gray-300
            borderRadius: "6px", // rounded-md
            boxShadow: "0px 1px 2px rgba(0, 0, 0, 0.05)", // shadow-sm
        },
        gridContainer: {
            display: "grid",
            gridTemplateColumns: "1fr", // Single column by default
            gap: "16px", // gap-4
        },
        gridContainerMd: {
            display: "grid",
            gridTemplateColumns: "1fr 1fr", // Two columns for medium screens
            gap: "16px",
        },
        selectDropdown: {
            backgroundColor: "#ffffff", //white
            cursor: "pointer",
            color: '#9CA3C3', //grey of default input to match.
        },
        selectPlaceholder: {
            color: "#6b7280", // Gray color for placeholder text
        },
        selectOption: {
            backgroundColor: "#f9fafb",
            color: "#1f2937",
            padding: "8px",
        },

        // Table presets
        tableWrapper: {
            backgroundColor: "#ffffff",
            borderRadius: "8px",
            boxShadow: "0 1px 2px 0 rgba(0, 0, 0, 0.05)",
            border: "1px solid #e5e7eb",
            overflow: "hidden",
        },
        tableContainer: {
            overflowX: "auto",
        },
        table: {
            minWidth: "100%",
            borderCollapse: "collapse",
            width: "100%",
        },
        tableHeadRow: {
            backgroundColor: "#f9fafb",
            borderBottom: "1px solid #e5e7eb",
            textAlign: "left",
        },
        tableHeader: {
            padding: "12px 24px", // px-6 py-3
            fontSize: "12px", // text-xs
            fontWeight: "600", // font-medium
            textTransform: "uppercase",
            letterSpacing: "0.05em", // tracking-wider
            color: "#6b7280", // text-gray-500
            cursor: "pointer",
            transition: "background-color 0.15s ease-in-out",
        },
        tableHeaderHover: {
            backgroundColor: "#f3f4f6", // hover:bg-gray-100
        },
        tableBody: {
            backgroundColor: "#ffffff",
        },
        tableRow: {
            transition: "background-color 0.15s ease-in-out",
        },
        tableRowHover: {
            backgroundColor: "#f9fafb", // hover:bg-gray-50
        },
        tableCell: {
            padding: "16px 24px", // px-6 py-4
            whiteSpace: "nowrap",
            fontSize: "14px", // text-sm
            color: "#111827", // text-gray-900
        },
        tableCellSecondary: {
            padding: "16px 24px",
            whiteSpace: "nowrap",
            fontSize: "14px",
            color: "#111827",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
        },
        tableCellWrapped: {
            maxWidth: "200px",
            wordWrap: "break-word",
            overflowWrap: "break-word",
            whiteSpace: "normal",
            padding: "16px 24px",
        },
        actionButtonsContainer: {
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "12px", // space-x-3

        },
        actionButton: {
            transition: "color 0.15s ease-in-out",
            cursor: "pointer",
        },
        actionButtonBlue: {
            color: "#2563eb", // text-blue-600
        },
        actionButtonBlueHover: {
            color: "#1e40af", // hover:text-blue-900
        },
        actionButtonGreen: {
            color: "#16a34a", // text-green-600
        },
        actionButtonGreenHover: {
            color: "#14532d", // hover:text-green-900
        },
        actionButtonGray: {
            color: "#4b5563", // text-gray-600
        },
        actionButtonGrayHover: {
            color: "#1f2937", // hover:text-gray-900
        },
        paginationButton: {
            backgroundColor: "#007bff",
            color: "white",
            border: "none",
            padding: "8px 12px",
            margin: "0 5px",
            borderRadius: "5px",
            cursor: "pointer",
            fontSize: "14px",
            fontWeight: "600",
        },

        // Info rows
        row: {
            flexDirection: 'row',
            marginBottom: 6,
        },
        colLeft: {
            width: '40%',
            paddingRight: 6,
        },
        colRight: {
            width: '60%',
            paddingLeft: 6,
        },
        label: {
            fontSize: 14,
            fontWeight: '600',
            color: '#374151',
        },
        value: {
            fontSize: 14,
            fontWeight: '400',
            color: '#374151',
        },

        // Satisfied bond container and button styles
        satisfiedContainer: {
            marginTop: 12,
            backgroundColor: '#ECF9EC',
            padding: 8,
            borderRadius: 6,
        },
        satisfiedText: {
            fontSize: 13,
            fontWeight: '600',
            color: 'green',
            marginBottom: 8,
        },
        satisfiedButton: {
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'green',
            paddingVertical: 8,
            borderRadius: 4,
        },
        satisfiedButtonText: {
            color: '#fff',
            fontSize: 13,
            fontWeight: '600',
        },

        //Modal
        overlay: {
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 1000,
        },
        modal: {
            backgroundColor: "#fff",
            padding: "20px",
            borderRadius: "8px",
            boxShadow: "0px 4px 6px rgba(0, 0, 0, 0.1)",
            position: "relative",

            width: "90%",
            maxWidth: "800px",
            maxHeight: "90vh",
            overflowY: "auto",
            overflowX: "auto",
        },
        closeButton: {
            position: "absolute",
            top: "10px",
            right: "15px",
            border: "none",
            background: "none",
            fontSize: "20px",
            cursor: "pointer",
        },

        // List
        listItem: {
            display: "flex",
            alignItems: "center",
            padding: "16px",
            backgroundColor: "#fff",
            marginBottom: "12px",
            border: "1px solid #f9fafb",
            borderRadius: "8px",
            position: "relative",
        },
        completedItem: {
            backgroundColor: "#f0fdf4", // success.backgroundColor
            border: "1px solid #bbf7d0", // success.borderColor
        },
        pendingItem: {
            backgroundColor: "#FFF8CC",
            border: "1px solid #FFF8CC",
        },
        listItemIcon: {
            marginRight: "12px",
        },
        listItemTitle: {
            fontSize: "16px",
            fontWeight: "500",
            color: "#184A89", // colors.primary
            //marginBottom: 4,
        },
        listItemDescription: {
            fontSize: "14px",
            //color: "", // colors.default.text is regular black text?
        },
        checkmarkContainer: {
            position: "absolute",
            //top: 8,
            //right: 8,
            backgroundColor: "#f0fdf4",
            borderRadius: "20px",
           // padding: 4,
        },
        pendingIconContainer: {
            position: "absolute",
            //top: 8,
            //right: 8,
            backgroundColor: "#FFF8CC",
            borderRadius: "20px",
            //padding: 4,
        },
        disabledOverlay: {
            opacity: 0.5,
        },

    },
};

const STORAGE_KEY = 'safepathweb-theme';

function getInitialMode() {
    try {
        const stored = window.localStorage.getItem(STORAGE_KEY);
        if (stored === 'dark' || stored === 'light') return stored;
    } catch {
        // ignore (privacy mode, disabled storage, etc.)
    }

    // Default to light mode unless user explicitly chose otherwise.
    return 'light';
}

const ThemeContext = createContext({
    ...theme,
    mode: 'light',
    isDark: false,
    setMode: () => {},
    toggleMode: () => {},
});

export const ThemeProvider = ({ children }) => {
    const [mode, setMode] = useState(getInitialMode);

    useEffect(() => {
        // Apply to document for CSS selectors.
        document.documentElement.setAttribute('data-theme', mode);

        // Optional: add a class hook as well (useful if we ever want class-based theming).
        document.documentElement.classList.toggle('dark', mode === 'dark');

        try {
            window.localStorage.setItem(STORAGE_KEY, mode);
        } catch {
            // ignore
        }
    }, [mode]);

    const value = useMemo(() => {
        const isDark = mode === 'dark';
        const toggleMode = () => setMode((m) => (m === 'dark' ? 'light' : 'dark'));
        return {
            ...theme,
            mode,
            isDark,
            setMode,
            toggleMode,
        };
    }, [mode]);

    return (
        <ThemeContext.Provider value={value}>
            {children}
        </ThemeContext.Provider>
    );
};

export const useTheme = () => useContext(ThemeContext);
